'use client';

import { useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Upload, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { tsvToCsvContent } from '../../data/tsv-to-csv-content';

const sampleTsv = `name\tage\tcity\tisActive\tskills
John Doe\t30\tNew York\ttrue\t"JavaScript, TypeScript, React"
Jane Smith\t25\tSan Francisco\tfalse\t"Python, Data Analysis"
Bob Johnson\t35\tChicago\ttrue\t"Java, Spring, SQL"`;

const sampleCsv = `name,age,city,isActive,skills
"John Doe",30,New York,true,"JavaScript, TypeScript, React"
"Jane Smith",25,San Francisco,false,"Python, Data Analysis"
"Bob Johnson",35,Chicago,true,"Java, Spring, SQL"`;

function convertTsvToCsv(tsvString: string, outputDelimiter = ','): string {
    try {
        if (!tsvString.trim()) {
            return '';
        }

        // Parse TSV to JSON using PapaParse
        const results = Papa.parse(tsvString, {
            delimiter: '\t',
            skipEmptyLines: true,
        });

        if (results.errors.length > 0) {
            throw new Error(results.errors[0].message);
        }

        // Convert to CSV with specified delimiter
        return Papa.unparse(results.data, {
            delimiter: outputDelimiter,
            quotes: true,
            skipEmptyLines: true,
        });
    } catch (error) {
        throw new Error(`Error converting TSV to CSV: ${error instanceof Error ? error.message : 'Invalid TSV'}`);
    }
}

export default function TsvToCsvTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [outputDelimiter, setOutputDelimiter] = useState(',');
    const [isConverting, setIsConverting] = useState(false);
    const { toast } = useToast();

    const convertTsv = useCallback((tsvString: string) => {
        try {
            if (!tsvString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            setIsConverting(true);
            const csvString = convertTsvToCsv(tsvString, outputDelimiter);
            setOutput(csvString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid TSV'}`);
            setOutput('');
        } finally {
            setIsConverting(false);
        }
    }, [outputDelimiter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            convertTsv(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input, outputDelimiter, convertTsv]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);

        // Reset the file input
        e.target.value = '';
    };

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The CSV has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName ? fileName.replace(/\.\w+$/, '.csv') : 'converted_data.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
        setFileName(null);
    };

    const loadSample = () => {
        setInput(sampleTsv);
        setFileName('sample.tsv');
    };

    const handleOutputDelimiterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setOutputDelimiter(e.target.value);
    };

    const swapInputOutput = () => {
        if (output) {
            setInput(output);
            setOutput('');
            setFileName(prev => prev ? prev.replace(/\.(tsv|csv)$/, ext =>
                ext === '.tsv' ? '.csv' : '.tsv'
            ) : null);
        }
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="TSV to CSV Converter - Free Online Data Format Converter">
                Convert TSV (Tab-Separated Values) to CSV (Comma-Separated Values) format instantly. Perfect for data processing and spreadsheet applications.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="flex-1 sm:flex-none">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Output Delimiter
                            </label>
                            <select
                                value={outputDelimiter}
                                onChange={handleOutputDelimiterChange}
                                className="w-full sm:w-32 bg-background border border-input rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50 focus:outline-none"
                                disabled={isConverting}
                            >
                                <option value=",">, (Comma)</option>
                                <option value="\t">\t (Tab)</option>
                                <option value=";">; (Semicolon)</option>
                                <option value="|">| (Pipe)</option>
                            </select>
                        </div>

                        <div className="relative flex-1">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Upload File
                            </label>
                            <label className="flex items-center justify-center w-full px-4 py-2 bg-background border border-dashed border-input rounded-md cursor-pointer hover:bg-accent/50 transition-colors">
                                <Upload className="h-4 w-4 mr-2" />
                                <span className="text-sm truncate max-w-[200px]">
                                    {fileName || 'Choose a file...'}
                                </span>
                                <input
                                    type="file"
                                    accept=".tsv,.csv,text/tab-separated-values,text/csv"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                    disabled={isConverting}
                                />
                            </label>
                            {fileName && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFileName(null);
                                        setInput('');
                                    }}
                                    className="absolute right-2 top-8 p-1 text-muted-foreground hover:text-foreground"
                                    title="Remove file"
                                    disabled={isConverting}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadSample}
                            className="gap-1.5"
                            disabled={isConverting}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Sample
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            disabled={!input || isConverting}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        TSV Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName || 'Paste your TSV data here'}
                                    </CardDescription>
                                </div>
                                <div className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                                    TSV (Tab-separated)
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your TSV data here...\n' +
                                        'name\tage\tcity\tisActive\tskills\n' +
                                        'John Doe\t30\tNew York\ttrue\t"JavaScript, TypeScript"\n' +
                                        'Jane Smith\t25\tSan Francisco\tfalse\tPython'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                    disabled={isConverting}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-4 lg:my-0">
                    <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full h-12 w-12"
                        onClick={swapInputOutput}
                        disabled={!output || isConverting}
                        title="Swap input and output"
                    >
                        <ArrowRight className="h-5 w-5" />
                        <ArrowLeft className="h-5 w-5 absolute" />
                    </Button>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        CSV Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName ?
                                            fileName.replace(/\.\w+$/, '.csv') :
                                            'Your converted CSV will appear here'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!output || isConverting}
                                        className="gap-1.5"
                                    >
                                        <FileText className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output || isConverting}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve formatting
                                    )}
                                    placeholder="Your converted CSV will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your converted CSV will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                                {isConverting && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={tsvToCsvContent.title}
                    description={tsvToCsvContent.description}
                    content={tsvToCsvContent.content}
                    faqs={tsvToCsvContent.faqs}
                    relatedTools={tsvToCsvContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
